import type { BlogPostMeta } from "@/lib/blog.types";

/**
 * Exactly the post metadata `/changelog` renders — a link, a date, a title and
 * a standfirst.
 *
 * A narrowed view rather than the whole `BlogPostMeta`: loader data is
 * dehydrated into the HTML of every locale's page, and `excerpt`/`tags`/
 * `authors`/`keywords`/`image`/`pinned` are ~75% of a post meta's bytes and
 * render nowhere on this page.
 */
export type ChangelogEntry = Pick<
  BlogPostMeta,
  "slug" | "year" | "month" | "day" | "date" | "title" | "description"
>;

/** A year's worth of changelog entries, in the order they were published. */
interface ChangelogYearGroup {
  year: string;
  posts: ChangelogEntry[];
}

/**
 * Split an already newest-first post stream into year buckets, preserving the
 * incoming order both between and within buckets.
 *
 * Deliberately order-preserving rather than re-sorting: the `_index/by-tag`
 * shard written by `scripts/build-blog-index.ts` is the single source of truth
 * for post order, and a second sort here could silently disagree with the feed
 * and the tag archive. A stream that ever arrives out of order therefore shows
 * up as a repeated year heading rather than as a quietly different ordering on
 * each surface.
 */
export function groupPostsByYear(
  posts: readonly ChangelogEntry[],
): ChangelogYearGroup[] {
  const groups: ChangelogYearGroup[] = [];
  for (const post of posts) {
    const last = groups.at(-1);
    if (last?.year === post.year) last.posts.push(post);
    else groups.push({ year: post.year, posts: [post] });
  }
  return groups;
}
